import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Label from '@components/inputs/text/Label';
import DropDownMenu, { IChoice } from "@components/inputs/DropDownMenu";
import nationalities from '../../SmartOpsHome/nationalities';
import { isNotNullNotUndefined } from '@utils/misc/functions';
import { textBoxStyle } from '@utils/styles/textboxStyle';
import { NavBar } from "@components/navigation/NavBar/NavBar";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { OnboardingPersonalState, OnboardingPersonalAction } from './onboardingPersonalReducer';
import { Country } from "@utils/constants/localisation";

interface FlexiOwnPersonalDetailsV2Props {
  navigateTarget: string;
  onboardingPersonalState: OnboardingPersonalState;
  onboardingPersonalDispatch: React.Dispatch<OnboardingPersonalAction>;
  country: Country;
}

export default function FlexiOwnPersonalDetailsV2({
  navigateTarget,
  onboardingPersonalState,
  onboardingPersonalDispatch,
  country
}: FlexiOwnPersonalDetailsV2Props) {
  const navigate = useNavigate();
  const dropDownPleaseSelect: IChoice = { label: '- please select -', value: '', enabled: false };

  const sexOptions: IChoice[] = [
    { value: 'Male', label: 'Male', enabled: true },
    { value: 'Female', label: 'Female', enabled: true },
    { value: 'Other', label: 'Other', enabled: true },
    { value: 'Do not wish to disclose', label: 'Do not wish to disclose', enabled: true },
  ];

  const yesOption: IChoice = { value: 'true', label: 'Yes', enabled: true };
  const noOption: IChoice = { value: 'false', label: 'No', enabled: true };
  const hasAltNameOptions: IChoice[] = [yesOption, noOption];

  const [hasAltName, setHasAltName] = useState<Boolean>(
    onboardingPersonalState.alternativeName !== '' && isNotNullNotUndefined(onboardingPersonalState.alternativeName)
  );

  function getDefaultGender(sexOptions: IChoice[]): IChoice {
    return onboardingPersonalState.gender === ''
      ? sexOptions[0]
      : sexOptions.find((gender) => gender.value === onboardingPersonalState.gender) || sexOptions[0];
  }

  function getDefaultNationality(nationalities: IChoice[]): IChoice | undefined {
    return country == Country.AU
      ? nationalities.find((nationality) => nationality.label === 'Australian')
      : nationalities.find((nationality) => nationality.label === 'British');
  }

  const defaultNationality = getDefaultNationality(nationalities);
  const defaultValue = defaultNationality ? { label: defaultNationality.label, value: defaultNationality.value } : dropDownPleaseSelect;
  const defaultGender = getDefaultGender(sexOptions);

  useEffect(() => {
    if (!onboardingPersonalState.gender) {
      onboardingPersonalDispatch({ type: "SET_GENDER", payload: defaultGender?.value });
    }
    if (!onboardingPersonalState.nationality) {
      onboardingPersonalDispatch({ type: "SET_NATIONALITY", payload: defaultValue?.value });
    }
  }, []);

  const handlePersonalInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.currentTarget.name) {
      case 'firstName':
        onboardingPersonalDispatch({ type: "SET_FIRST_NAME", payload: e.currentTarget.value });
        break;
      case 'middleName':
        onboardingPersonalDispatch({ type: "SET_MIDDLE_NAME", payload: e.currentTarget.value });
        break;
      case 'lastName':
        onboardingPersonalDispatch({ type: "SET_LAST_NAME", payload: e.currentTarget.value });
        break;
      case 'email':
        onboardingPersonalDispatch({ type: "SET_EMAIL", payload: e.currentTarget.value });
        break;
      case 'hasAltName':
        if (e.currentTarget.value === 'false') {
          setHasAltName(false);
          onboardingPersonalDispatch({ type: "SET_ALTERNATIVE_NAME", payload: '' });
        } else {
          setHasAltName(true);
        }
        break;
      case 'altName':
        onboardingPersonalDispatch({ type: "SET_ALTERNATIVE_NAME", payload: e.currentTarget.value });
        break;
      case 'genderDropMenu':
        onboardingPersonalDispatch({ type: "SET_GENDER", payload: e.currentTarget.value });
        break;
      case 'nationality':
        onboardingPersonalDispatch({ type: "SET_NATIONALITY", payload: e.currentTarget.value });
        break;
      case 'hirerABN':
        onboardingPersonalDispatch({ type: "SET_HIRER_ABN", payload: e.currentTarget.value });
        break;
      case 'tollTagNumber':
        onboardingPersonalDispatch({ type: "SET_TOLL_TAG_NUMBER", payload: e.currentTarget.value });
        break;
      case 'tollTagAccountNumber':
        onboardingPersonalDispatch({ type: "SET_TOLL_TAG_ACCOUNT_NUMBER", payload: e.currentTarget.value });
        break;
      case 'tollTagIssuer':
        onboardingPersonalDispatch({ type: "SET_TOLL_TAG_ISSUER", payload: e.currentTarget.value });
        break;
      case 'nationalInsuranceNumber':
        onboardingPersonalDispatch({ type: "SET_NATIONAL_INSURANCE_NUMBER", payload: e.currentTarget.value });
        break;
    }
  };

  const handleInputABN = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^0-9]/g, '');
    if (value.length > 11) {
      value = value.slice(0, 11);
    }
    event.target.value = value;
  };

  const handleInputNI = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^a-zA-Z0-9]/g, '');
    if (value.length > 9) {
      value = value.slice(0, 9);
    }
    event.target.value = value;
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    navigate(navigateTarget);
  };

  return (
    <>
      <NavBar pageTitle={"Personal Details"} backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={onSubmit}>
        <p style={{ margin: '2.5vh 0' }}>
          Please review the customer's <strong>personal details</strong>, ensuring that each field is populated with the correct data.
        </p>

        <div style={{ margin: '2.5vh 0' }}>
          <Label text={'First Name'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
          <input
            name={'firstName'}
            style={textBoxStyle}
            required
            defaultValue={onboardingPersonalState.firstName}
            onChange={handlePersonalInfoChange}
          />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
          <Label text={'Middle Name'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={false} />
          <input
            name={'middleName'}
            style={textBoxStyle}
            defaultValue={onboardingPersonalState.middleName}
            onChange={handlePersonalInfoChange}
          />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
          <Label text={'Last Name'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
          <input
            name={'lastName'}
            style={textBoxStyle}
            required
            defaultValue={onboardingPersonalState.lastName}
            onChange={handlePersonalInfoChange}
          />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
          <Label text={'Email'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
          <input
            name={'email'}
            style={textBoxStyle}
            type='email'
            required
            defaultValue={onboardingPersonalState.email}
            disabled
          />
        </div>

        <p style={{ margin: '2.5vh 0' }}>If you need to change the email address, please do so directly in the CRM.</p>

        <div style={{ margin: '2.5vh 0' }}>
          <Label text={'Are you known by an alternative name?'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
          <DropDownMenu
            menuName='hasAltName'
            defaultVal={hasAltName === true ? yesOption : noOption}
            required={true}
            choices={hasAltNameOptions}
            onSelect={handlePersonalInfoChange}
          />
        </div>

        {hasAltName == true && (
          <div style={{ margin: '2.5vh 0' }}>
            <Label text={'Please specify an alternative name'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
            <input
              name='altName'
              style={textBoxStyle}
              required
              defaultValue={onboardingPersonalState.alternativeName}
              onChange={handlePersonalInfoChange}
            />
          </div>
        )}

        <div style={{ margin: '2.5vh 0' }}>
          <Label text={'Sex'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
          <DropDownMenu
            menuName='genderDropMenu'
            defaultVal={{ label: onboardingPersonalState.gender || defaultGender.value, value: onboardingPersonalState.gender || defaultGender.value }}
            required={true}
            choices={sexOptions.map((item: any) => ({ label: item.label, value: item.value, enabled: true }))}
            onSelect={handlePersonalInfoChange}
          />
        </div>

        <div style={{ margin: '2.5vh 0' }}>
          <Label text={'Nationality'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
          <DropDownMenu
            menuName='nationality'
            defaultVal={
              nationalities.some((item) => item.value === onboardingPersonalState.nationality)
                ? { label: onboardingPersonalState.nationality, value: onboardingPersonalState.nationality }
                : defaultValue
            }
            required={true}
            choices={[dropDownPleaseSelect, ...nationalities.map((item: any) => ({ label: item.label, value: item.value, enabled: true }))]}
            onSelect={handlePersonalInfoChange}
          />
        </div>

        {country == Country.AU && (
          <>
            <div style={{ margin: '2.5vh 0' }}>
              <Label text={'Hirer ABN (must be 11 digits)'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
              <input
                name={'hirerABN'}
                pattern="\d{11}"
                type='text'
                style={textBoxStyle}
                required
                defaultValue={onboardingPersonalState.hirerABN}
                onInput={handleInputABN}
                onChange={handlePersonalInfoChange}
              />
            </div>

            <div style={{ margin: '2.5vh 0' }}>
              <Label text={'Toll tag issuer'} styleCfg={{ largeFont: false, bold: false }} />
              <input
                name={'tollTagIssuer'}
                style={textBoxStyle}
                defaultValue={onboardingPersonalState.tollTagIssuer}
                onChange={handlePersonalInfoChange}
              />
            </div>

            <div style={{ margin: '2.5vh 0' }}>
              <Label text={'Toll tag account number'} styleCfg={{ largeFont: false, bold: false }} />
              <input
                name={'tollTagAccountNumber'}
                style={textBoxStyle}
                defaultValue={onboardingPersonalState.tollTagAccountNumber}
                onChange={handlePersonalInfoChange}
              />
            </div>

            <div style={{ margin: '2.5vh 0' }}>
              <Label text={'Toll tag number'} styleCfg={{ largeFont: false, bold: false }} />
              <input
                name={'tollTagNumber'}
                style={textBoxStyle}
                defaultValue={onboardingPersonalState.tollTagNumber}
                onChange={handlePersonalInfoChange}
              />
            </div>
          </>
        )}

        {country == Country.GB && (
          <div style={{ margin: '2.5vh 0' }}>
            <Label text={'National Insurance Number'} styleCfg={{ largeFont: false, bold: false }} requiredFieldInd={true} />
            <input
              name={'nationalInsuranceNumber'}
              style={textBoxStyle}
              required
              defaultValue={onboardingPersonalState.nationalInsuranceNumber}
              onInput={handleInputNI}
              onChange={handlePersonalInfoChange}
            />
          </div>
        )}

        <SubmitBtn text={"Next"} />
      </form>
    </>
  );
}